import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { platformApiFetch, getPlatformToken, setPlatformToken } from '../platform/platformApi';

const PlatformAuthContext = createContext(null);

export function PlatformAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refreshMe = useCallback(async () => {
    const token = getPlatformToken();
    if (!token) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const res = await platformApiFetch('/api/platform/auth/me');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPlatformToken(null);
        setUser(null);
      } else {
        setUser(data.user);
      }
    } catch (_e) {
      setPlatformToken(null);
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async ({ email, password }) => {
    const res = await platformApiFetch('/api/platform/auth/login', {
      method: 'POST',
      body: { email, password }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }
    setPlatformToken(data.token);
    setUser(data.user);
    setReady(true);
  }, []);

  const logout = useCallback(() => {
    setPlatformToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, logout, refreshMe }),
    [user, ready, login, logout, refreshMe]
  );

  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}

export function usePlatformAuth() {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) {
    throw new Error('usePlatformAuth must be used within PlatformAuthProvider');
  }
  return ctx;
}
